import { StoreNode } from './storeNode';
import { Nullable } from '../core';

export type MessageMode =
  /** Message that should reach all nodes in the tree. */
  'broadcast' |
  /** Message that should propagate through the parent hierarchy in the tree. */
  'emit' |
  /** Message that should reach the store. */
  'dispatch';

export type Message<TNode extends StoreNode = StoreNode> = {
  type: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- message payload is intentionally untyped to support arbitrary data across the message bus
  payload: any,
  mode: MessageMode,
  fullType: string,
  sender: TNode,
  senderName: Nullable<string>,
  target?: Nullable<string>
};

export type SerializedMessage = {
  type: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- serialized message payload is intentionally untyped
  payload: any,
  mode?: MessageMode,
  fullType?: string,
  senderName?: string
};

export type MessageHandler<TNode extends StoreNode = StoreNode> =
  (msg: Message<TNode>) => boolean | void | Promise<boolean> | Promise<void>;

export enum MessageHandlerWaitMode {
  None = 'None',
  Serial = 'Serial',
  Parallel = 'Parallel',
  First = 'First'
}

export function createMessage<TNode extends StoreNode = StoreNode>(
  node: StoreNode | null,
  mode: MessageMode,
  type: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- payload is intentionally untyped to support arbitrary data
  payload?: any,
  source?: unknown): Message<TNode> {

  const prefix = node?.nodeType;
  const msg: Message<TNode> = {
    type,
    payload,
    mode,
    fullType: (prefix ? (prefix + ':') : '') + type,
    // @ts-expect-error -- Message.sender is typed as TNode but node may be a broader StoreNode type
    sender: node || null,
    senderName: node?.nodeType || null,
    source: source || null
  };

  return msg;
}

export function serializeMessage(msg: Message) {

  return {
    type: msg.type,
    payload: msg.payload,
    mode: msg.mode,
    fullType: msg.fullType,
    senderName: msg.senderName
  };
}

export function deserializeMessage(data: SerializedMessage) {

  return createMessage(
    null,
    data.mode || 'emit',
    data.type,
    data.payload);
}

export type Memo = {
  type: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- memo payload is intentionally untyped
  payload?: any;
  error?: Error;
}